// External Imports
import { useState } from "react";
import React from "react";
import { BsPlay } from "react-icons/bs";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import DetailPair from "../UI/DetailPair";
import { arrayToCsv } from "../../util/arrayToCsv";
import { MdDownload } from "react-icons/md";

/** ========================================================================
 * * Query Component
 * This component renders a page to directly query the database either with graphql or sql.
 * The requested data is displayed in a textarea by using JSON format.
 * The user can also download the data as a CSV file.
 * ========================================================================== */

export default function FBDCTesting() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState();

  /* ======================= FETCH ======================= 
  This function fetches the default columns for a given table.
  If an id is provided only that row is returned. If no Id is provided
  all rows are returned */

  const fetchRecord = async () => {
    try {
      setLoading(true);

      // Get Table and ID
      const table = document.getElementById("table").value;
      const id = document.getElementById("id").value;

      // Fetch Data
      let res;
      if (id) res = await requestAdminAPI("fetch", { table, id });
      else res = await requestAdminAPI("fetch-all", { table });

      setResults(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= EXECUTE GRAPHQL/SQL QUERY ======================= */
  const execute = async () => {
    try {
      setLoading(true);

      const queryType = document.getElementById("queryType").value;
      const query = document.getElementById("query").value;

      let res = {};
      if (queryType === "graphql")
        res = await requestAdminAPI("query-gql", { query });
      else res = await requestAdminAPI("query-sql", { query });

      // If first item in res object is an array
      if (res && Array.isArray(res[Object.keys(res)[0]])) {
        document.getElementById("path").value = Object.keys(res)[0];
      }
      setResults(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= CUSTOM FUNCTION ======================= */
  const customFunction = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("custom");
      setResults(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  /* ======================= DOWNLOAD CSV ======================= */
  const downnloadCSV = async () => {
    const path = document.getElementById("path").value;
    let toDownload = results;
    if (path) {
      path.split(".").forEach((key) => {
        toDownload = toDownload[key];
      });
    }
    arrayToCsv(toDownload, path || "results");
  };

  /* ======================= DOWNLOAD PDF ======================= */
  const downloadPDF = async () => {
    try {
      setLoading(true);
      const url = await requestAdminAPI("get-daily-report");
      window.open(url, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <Heading
        title="Database Query"
        isBackArrow={true}
        text="This page allows typing queries and mutations to interact with the database. Please use with caution."
      >
        <BsPlay className="icon" onClick={customFunction} />
        <MdDownload className="icon" onClick={downloadPDF} />
      </Heading>
      <div className="grid items-start gap-4 text-sm md:grid-cols-2">
        <div className="grid gap-4">
          <div className="flex items-end space-x-4 rounded-md">
            <DetailPair label="TABLE" edit={true} />
            <DetailPair label="ID" edit={true} />
            <button onClick={fetchRecord} className="btn btn-primary">
              Search
            </button>
          </div>
          <div className="grid gap-4">
            <div className="flex items-center space-x-4">
              <p className="text-stone-400">QUERY:</p>
              <select
                className="text-sm border input border-stone-600"
                id="queryType"
              >
                <option value="graphql">GraphQL</option>
                <option value="sql">SQL</option>
              </select>
            </div>
            <textarea
              className="border outline-none input border-stone-600"
              cols={5}
              rows={10}
              id="query"
              placeholder="You can get help writing a query by clicking the '?' icon on the top right of the page and asking POSHBot..."
            />
            <div className="flex space-x-4">
              <button onClick={execute} className="btn-primary">
                Execute
              </button>
              <input
                className="border input border-stone-600"
                id="path"
                placeholder="Path to array"
              />
              <button onClick={downnloadCSV} className="btn-secondary">
                Download
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinnerWrapper />
        ) : (
          <textarea
            className="p-4 rounded-lg outline-none no-scrollbar"
            cols={5}
            rows={24}
            value={results ? JSON.stringify(results, null, 2) : ""}
            onChange={() => {}}
          />
        )}
      </div>
    </div>
  );
}
